import React, { useEffect, useState } from 'react';
import { Input, Checkbox } from 'antd';
import { Info, RefreshCw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  findAccountForRoles,
  setAccountRoles,
  resetAuthenticator,
  ROLE_LABEL,
  AccountRolesInfo,
  listAdmins,
  AdminRow,
} from '../../../../api/admin/role.api';
import { promptTotp } from '../../../../utils/admin-stepup';
import {
  AdminButton,
  AdminCard,
  AdminPageHeader,
  AdminSectionTitle,
  AdminStatusPill,
} from '../../ui';

const ROLE_DESC: Record<string, string> = {
  Mod: 'Duyệt bài, xử lý report, ẩn/khôi phục bài–comment, duyệt Cab xưa.',
  Accountant: 'Chỉ xem báo cáo tài chính (doanh thu / nạp / rút).',
  Monetization:
    'Chỉ quản lý Cab Ads + Cab Shop (Affiliate). Không thấy khu vực khác.',
  Admin: 'Vận hành hầu hết (kiểm duyệt, user, cosmetic, tài chính…).',
  SuperAdmin: 'Toàn quyền + gán vai trò cho tài khoản khác.',
  Viewer:
    'Xem mọi trang quản trị, không thao tác được, dữ liệu cá nhân bị che. Dùng cho designer hoặc người ngoài.',
};

const ManagementRoles = () => {
  const [email, setEmail] = useState('');
  const [account, setAccount] = useState<AccountRolesInfo | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [totp, setTotp] = useState('');
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  const loadAdmins = async () => {
    setLoadingList(true);
    try {
      const res = await listAdmins();
      setAdmins(res.admins || []);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        toast.error('Chỉ Super Admin mới xem được danh sách admin.');
      }
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const search = async (e0?: string) => {
    const e = (e0 ?? email).trim();
    if (!e) return;
    setLoading(true);
    setAccount(null);
    try {
      const info = await findAccountForRoles(e);
      setAccount(info);
      setSelected(info.roles || []);
    } catch (err: any) {
      const code = err?.response?.status;
      toast.error(
        code === 403
          ? 'Bạn không có quyền phân quyền (cần Super Admin).'
          : code === 404
          ? 'Không tìm thấy tài khoản với email này.'
          : 'Không tìm được tài khoản.'
      );
    } finally {
      setLoading(false);
    }
  };

  const editAdmin = (adminEmail: string) => {
    setEmail(adminEmail);
    search(adminEmail);
    // đưa panel chỉnh sửa lên tầm nhìn
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggle = (role: string) => {
    setSelected((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  // SuperAdmin reset Authenticator cho tài khoản mất thiết bị (bắt mã Authenticator của chính mình).
  const doResetAuth = async (adminEmail: string) => {
    if (
      !window.confirm(
        `Reset Authenticator cho ${adminEmail}? Tài khoản này sẽ cài lại Authenticator 1 lần.`
      )
    )
      return;
    const code = promptTotp(`reset Authenticator cho ${adminEmail}`);
    if (!code) return;
    try {
      await resetAuthenticator(adminEmail, code);
      toast.success(
        `Đã reset Authenticator cho ${adminEmail}. Họ vào trang Bảo mật cài lại 1 lần.`
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Reset thất bại.');
    }
  };

  const save = async () => {
    if (!account) return;
    if (totp.trim().length !== 6) {
      toast.error('Nhập mã 6 số từ app Authenticator để xác nhận đổi vai trò.');
      return;
    }
    setSaving(true);
    try {
      const res = await setAccountRoles(account.email, selected, totp.trim());
      setSelected(res.roles || []);
      setAccount({ ...account, roles: res.roles || [] });
      setTotp('');
      toast.success(
        'Đã cập nhật vai trò. Tài khoản cần đăng nhập lại để áp quyền mới.'
      );
      loadAdmins();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (err?.response?.status === 403
          ? 'Bạn không có quyền phân quyền (cần Super Admin).'
          : 'Cập nhật vai trò thất bại.');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Phân quyền admin"
        description="Gán vai trò cho tài khoản để quyết định họ vào được những mục nào trong CMS. Chỉ Super Admin dùng được trang này."
      />

      <AdminCard>
        <div className="flex gap-3">
          <Input
            className="adm-search"
            prefix={<Search size={16} />}
            placeholder="Nhập email tài khoản…"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onPressEnter={() => search()}
            allowClear
          />
          <AdminButton loading={loading} onClick={() => search()}>
            Tìm
          </AdminButton>
        </div>
      </AdminCard>

      {/* ===== Danh sách tài khoản admin (chỉ Super Admin xem được) ===== */}
      <section className="adm-stack">
        <AdminSectionTitle
          title={`Danh sách admin (${admins.length})`}
          extra={
            <AdminButton
              variant="soft"
              size={36}
              icon={<RefreshCw size={16} />}
              onClick={loadAdmins}
              loading={loadingList}
            >
              Tải lại
            </AdminButton>
          }
        />
        <AdminCard flush>
          {admins.length === 0 && !loadingList && (
            <div className="p-5 text-sm adm-muted">
              Chưa có tài khoản admin.
            </div>
          )}
          {admins.map((a, idx) => (
            <div
              key={a.email}
              className="flex items-center justify-between gap-3 px-5 py-4"
              style={
                idx > 0 ? { borderTop: '1px solid var(--adm-line)' } : undefined
              }
            >
              <div className="min-w-0">
                <div
                  className="font-semibold truncate"
                  style={{ color: 'var(--adm-heading)' }}
                >
                  {a.email}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {a.roles.map((r) => (
                    <AdminStatusPill
                      key={r}
                      tone={r === 'SuperAdmin' ? 'warning' : 'brand'}
                    >
                      {ROLE_LABEL[r] || r}
                    </AdminStatusPill>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <AdminButton
                  size={32}
                  variant="danger-soft"
                  onClick={() => doResetAuth(a.email)}
                >
                  Reset Authen
                </AdminButton>
                <AdminButton
                  size={32}
                  variant="outline"
                  onClick={() => editAdmin(a.email)}
                >
                  Điều chỉnh
                </AdminButton>
              </div>
            </div>
          ))}
        </AdminCard>
        <p className="flex items-center gap-2 text-[13px] adm-muted">
          <Info size={16} />
          Bấm “Điều chỉnh” để đổi vai trò / gỡ khỏi admin (bỏ chọn hết = kick).
          Mọi thay đổi cần mã Authenticator để xác nhận.
        </p>
      </section>

      {account && (
        <AdminCard>
          <div className="mb-4">
            <div
              className="font-semibold"
              style={{ color: 'var(--adm-heading)' }}
            >
              {account.email}
            </div>
            <div className="text-[13px] adm-muted">
              Vai trò hiện tại:{' '}
              {account.roles.length
                ? account.roles.map((r) => ROLE_LABEL[r] || r).join(', ')
                : 'chưa có vai trò admin'}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {(account.assignableRoles || []).map((role) => (
              <label
                key={role}
                className="adm-panel flex items-start gap-3 cursor-pointer"
              >
                <Checkbox
                  checked={selected.includes(role)}
                  onChange={() => toggle(role)}
                />
                <div>
                  <div
                    className="font-semibold"
                    style={{ color: 'var(--adm-heading)' }}
                  >
                    {ROLE_LABEL[role] || role}
                  </div>
                  <div className="text-[13px] adm-muted">{ROLE_DESC[role]}</div>
                </div>
              </label>
            ))}
          </div>

          <div className="adm-field mt-5">
            <span>Mã Authenticator (6 số) — bắt buộc để đổi vai trò:</span>
            <Input
              value={totp}
              onChange={(e) =>
                setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="123456"
              inputMode="numeric"
              style={{
                maxWidth: 200,
                textAlign: 'center',
                letterSpacing: '0.3em',
              }}
            />
          </div>

          <AdminButton
            className="mt-5"
            loading={saving}
            disabled={totp.trim().length !== 6}
            onClick={save}
          >
            Lưu vai trò
          </AdminButton>
        </AdminCard>
      )}
    </div>
  );
};

export default ManagementRoles;
